import cx from "classnames";
import { useEffect, useState } from "react";

import {
  NewTabWarning,
  getNewTabTitle,
} from "metabase/common/components/ExternalLink";
import { Link } from "metabase/common/components/Link";
import CS from "metabase/css/core/index.css";
import { Box, Button, Flex, Icon, Text, isValidIconName } from "metabase/ui";
import type { IconName } from "metabase-types/api";

import S from "./EmptyState.module.css";

// A screen reader only announces a *change* to an already-registered live
// region -- if the region and its content both appear in the same DOM
// commit (the common case here: an EmptyState is usually mounted already
// showing its final title/message), most screen readers won't announce
// anything, since there's no "before" state for them to have observed.
// Rendering the title/message from a plain effect (rather than inline
// during render) defers it until after React's initial commit -- in a
// real browser that's after paint, giving assistive tech a chance to
// register the (empty) region first; deliberately not
// requestAnimationFrame, which -- unlike a plain effect -- React Testing
// Library's `act()` doesn't flush synchronously, so it would silently
// break any existing synchronous assertion against this text. The
// aria-live/role attributes stay on the same element that already shows
// this content -- only its child text is deferred, so there's no second,
// hidden copy to make text queries ambiguous.
function useDeferredNode(node: React.ReactNode): React.ReactNode {
  const [deferred, setDeferred] = useState<React.ReactNode>("");

  useEffect(() => {
    setDeferred(node);
  }, [node]);

  return deferred;
}

// Don't break existing empty states
// TODO - remove these and update empty states with proper usage of illustrationElement
const LegacyIcon = ({ icon }: { icon: IconName }) =>
  icon ? <Icon name={icon} className={CS.textLight} size={40} /> : null;

const LegacyImage = ({
  image,
  imageHeight,
  imageClassName,
  message,
}: {
  image: string;
  imageHeight?: number;
  imageClassName?: string;
  message?: string;
}) =>
  image ? (
    <img
      src={`${image}.png`}
      width="300px"
      height={imageHeight}
      alt={message}
      srcSet={`${image}@2x.png 2x`}
      className={imageClassName}
    />
  ) : null;

type EmptyStateProps = {
  message?: React.ReactNode;
  title?: React.ReactNode;
  action?: React.ReactNode;
  link?: string;
  illustrationElement?: React.ReactNode;
  onActionClick?: () => void;
  className?: string;
  icon?: IconName;
  image?: string;
  spacing?: "sm" | "md";
};

export const EmptyState = ({
  title,
  message,
  action,
  link,
  illustrationElement,
  onActionClick,
  className,
  icon,
  image,
  spacing = "md",
  ...rest
}: EmptyStateProps) => {
  const deferredTitle = useDeferredNode(title);
  const deferredMessage = useDeferredNode(message);

  return (
    <div className={className}>
      <Flex direction="column" align="center" justify="center" ta="center">
        {illustrationElement && (
          <Box
            className={cx("empty-state-illustration", S.illustration)}
            data-spacing={spacing}
          >
            {illustrationElement}
          </Box>
        )}
        <div>
          {isValidIconName(icon) && <LegacyIcon icon={icon} {...rest} />}
          {image && <LegacyImage image={image} {...rest} />}
        </div>
        {title && (
          <h2 aria-live="polite" className={CS.textMedium}>
            {deferredTitle}
          </h2>
        )}
        {message && (
          <Text role="status" c="text-secondary" mt="xs">
            {deferredMessage}
          </Text>
        )}
      </Flex>
      {/* TODO - we should make this children or some other more flexible way to
      add actions
      */}
      <Flex>
        <Flex className={S.footer} align="center" mx="auto">
          {action && link && (
            <Link
              to={link}
              target={link.startsWith("http") ? "_blank" : ""}
              title={link.startsWith("http") ? getNewTabTitle() : undefined}
            >
              <Button variant="filled">{action}</Button>
              {link.startsWith("http") && <NewTabWarning />}
            </Link>
          )}
          {action && onActionClick && (
            <Button onClick={onActionClick} variant="filled">
              {action}
            </Button>
          )}
        </Flex>
      </Flex>
    </div>
  );
};
